import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter, input,
  Input,
  OnInit, output,
  Output, signal
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@ngneat/transloco';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import { SvgWrapperComponent } from '../svg-wrapper/svg-wrapper.component';
import { ShowMoreLessComponent } from '../show-more-less/show-more-less.component';
import { QuestMetadata, QuestStepMetadata } from '../../interfaces/quest-core.interfaces';
import { ImgInfoBody } from '../../interfaces/quest-info.interfaces';
import { getPlatform } from '../../functions/common.functions';
import { timeSpanToMs } from '../../functions/time.functions';
import { difficultyEnumToPrettyText, questAvailabilityToText } from '../../functions/quest.functions';
import { metersToPretty } from '../../functions/string.functions';
import { timeToPrettyText } from '../../functions/date.functions';
import { QuestMode } from '../../interfaces/quest-info.enums';

@Component({
    selector: 'app-quest-view',
    imports: [CommonModule, SvgWrapperComponent, TranslocoDirective, TranslocoPipe, ShowMoreLessComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './quest-view.component.html',
    styleUrl: './quest-view.component.scss'
})
export class QuestViewComponent implements OnInit, AfterContentInit {
  @Input() quest!: QuestMetadata;
  hideHeader = input<boolean>(false);

  hideDownloadIOS = signal(false);
  hideDownloadAndroid = signal(false);

  orgLogo: string | SafeHtml;

  requiredLevel = 1;

  images: ImgInfoBody[] = [];
  questDescription: string[] = [];
  stepsDescriptions: string[][] = [];

  @Output() hideEvent = new EventEmitter<void>();
  @Output() closeQuest = new EventEmitter<void>();
  previewImage = output<string>();

  constructor(private cdr: ChangeDetectorRef, private transloco: TranslocoService, private ds: DomSanitizer) {
    switch (getPlatform().toLowerCase()) {
      case 'android':
        this.hideDownloadIOS.set(true);
        break;
      case 'ios':
        this.hideDownloadAndroid.set(true);
        break;
    }
  }

  ngOnInit(): void {
    // TODO later test it on some quest
    if (this.quest.gameAccess) {
      for (const ga of this.quest.gameAccess) {
        if (ga.accessType === 'level') {
          this.requiredLevel = ga.config['level'];
          break;
        }
      }
    }

    if (this.quest.organization && this.quest.organization.secondaryLogo) {
      if (this.quest.organization.secondaryLogo.startsWith('http')) {
        this.orgLogo = this.quest.organization.secondaryLogo;
      } else {
        this.orgLogo = this.ds.bypassSecurityTrustHtml(this.quest.organization.secondaryLogo);
      }
    }

    this.images = this.quest.info.images.filter(image => image.url && !image.url.includes('preview'));

    if (this.quest.description && this.quest.description.length > 0 && this.quest.description[0].type === 'text') {
      this.questDescription = this.quest.description[0].data.split('\n');
    }

    for (const step of this.quest.steps) {
      if (step.functionalMetadata.clueMetadata
        && step.functionalMetadata.clueMetadata.length > 0
        && step.functionalMetadata.clueMetadata[0].type === 'text'
        && step.functionalMetadata.clueMetadata[0].data) {
        this.stepsDescriptions.push(step.functionalMetadata.clueMetadata[0].data.split('\n'));
      } else {
        this.stepsDescriptions.push(['']);
      }
    }
  }

  ngAfterContentInit(): void {
    if (this.images.length > 0) {
      setTimeout(() => {
        const swiperEl = document.querySelector('swiper-container') as any;
        swiperEl?.initialize();
      }, 0);
    }
  }

  getRating(): number {
    return Math.ceil(this.quest.info.rating.value);
  }

  getUserName(): string {
    if (this.quest.creator.isSuperUser) {
      return `QuestAtlas ${this.transloco.translate('team')}`;
    }

    if (this.quest.creator.firstName) {
      return `${this.quest.creator.firstName} ${this.quest.creator.lastName}`;
    }

    if (this.quest.creator.username) {
      return this.quest.creator.username;
    }

    return this.quest.creator.email;
  }

  hasStepTimer(step: QuestStepMetadata): boolean {
    return !!step.functionalMetadata.timer && timeSpanToMs(step.functionalMetadata.timer) > 0;
  }

  doesStepHaveStartZone(step: QuestStepMetadata): boolean {
    return !!step.functionalMetadata.startZone;
  }

  protected readonly questAvailabilityToText = questAvailabilityToText;
  protected readonly difficultyEnumToPrettyText = difficultyEnumToPrettyText;
  protected readonly metersToPretty = metersToPretty;
  protected readonly timeToPrettyText = timeToPrettyText;
  protected readonly QuestMode = QuestMode;
}
